import React, { useEffect, useMemo, useState } from "react";

import {
  Button,
  ArrowButton,
  Typography,
  InfoPanel,
  Modal,
} from "@king-ict/eupisi-ui/components";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";

import { useTypedSelector } from "../app/rootReducer";
import { useAppDispatch } from "../app/store";
import { ValidationFailure } from "../models";
import styles from "./Toolbar.module.scss";
import { resetSaveOK } from "../slices/applicationSlice";

interface DeleteModalMessages {
  deleteModalTitle?: string;
  deleteModalMessage?: string;
}

export interface ToolbarTopProps {
  children?: any;
  pageTitle?: string;
  pageSubTitle?: string;
  showBack?: "hide" | "disable" | "show";
  showDelete?: "hide" | "disable" | "show";
  showAdd?: "hide" | "disable" | "show";
  showEdit?: "hide" | "disable" | "show";
  showSave?: "hide" | "disable" | "show";
  showCancel?: "hide" | "disable" | "show";
  showCustom?: boolean;
  isEdited?: boolean;
  errors?: ValidationFailure[];
  deleteModalMessages?: DeleteModalMessages;
  onClickAdd?: (event: React.MouseEvent<any, MouseEvent>) => void;
  onClickEdit?: (event: React.MouseEvent<any, MouseEvent>) => void;
  onClickBack?: (event: React.MouseEvent<any, MouseEvent>) => void;
  onClickSave?: (event: React.MouseEvent<any, MouseEvent>) => void;
  onClickCancel?: (event: React.MouseEvent<any, MouseEvent>) => void;
  onClickDelete?: (event: React.MouseEvent<any, MouseEvent>) => void;
}
export const ToolbarTop = ({
  children,
  pageTitle,
  pageSubTitle,
  showBack,
  showAdd,
  showDelete,
  showEdit,
  showSave,
  showCancel,
  showCustom,
  errors,
  deleteModalMessages,
  onClickAdd,
  onClickEdit,
  onClickSave,
  onClickCancel,
  onClickBack,
  onClickDelete,
}: ToolbarTopProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const saveOK: boolean = useTypedSelector((state) => state.application.saveOK);

  const renderAddButton = useMemo(() => {
    switch (showAdd) {
      case "show":
        return (
          <Button
            className="mr-1 float-right"
            outline
            size="sm"
            onClick={onClickAdd}
          >
            {t("common.addNew")}
          </Button>
        );
      case "disable":
        return (
          <Button className="mr-1 float-right" outline size="sm" disabled>
            {t("common.addNew")}
          </Button>
        );
      default:
        return <></>;
    }
  }, [t, showAdd, onClickAdd]);

  const renderDeleteButton = useMemo(() => {
    switch (showDelete) {
      case "show":
        const { deleteModalTitle, deleteModalMessage } =
          deleteModalMessages || {};

        return (
          <>
            <Button
              className="mr-1 float-right"
              color="danger"
              outline
              size="sm"
              onClick={() => setShowDeleteModal(true)}
            >
              {t("common.delete")}
            </Button>
            <Modal
              type={"confirm"}
              title={deleteModalTitle || t("common.deleteModalTitle")}
              cancelText={t("common.no")}
              confirmText={t("common.yes")}
              open={showDeleteModal}
              onClose={() => setShowDeleteModal(false)}
              onConfirm={onClickDelete}
            >
              {deleteModalMessage || t("common.deleteModalMessage")}
            </Modal>
          </>
        );
      case "disable":
        return (
          <Button className="mr-1 float-right" outline size="sm" disabled>
            {t("common.delete")}
          </Button>
        );
      default:
        return <></>;
    }
  }, [showDelete, onClickDelete, showDeleteModal, setShowDeleteModal]);

  const renderEditButton = useMemo(() => {
    switch (showEdit) {
      case "show":
        return (
          <Button
            className="mr-1 float-right"
            outline
            size="sm"
            onClick={onClickEdit}
          >
            {t("common.edit")}
          </Button>
        );
      case "disable":
        return (
          <Button className="mr-1 float-right" outline size="sm" disabled>
            {t("common.edit")}
          </Button>
        );
      default:
        return <></>;
    }
  }, [t, showEdit, onClickEdit]);

  const renderBackButton = () => {
    switch (showBack) {
      case "show":
        return (
          <>
            <ArrowButton
              ariaLabel={t("accessibility.backButton")}
              backwards
              onClick={onClickBack ? onClickBack : history.goBack}
              title={t("accessibility.backButton")}
            ></ArrowButton>
            <Typography
              variant="base"
              component="span"
              color="primary"
              className="mb-0"
            >
              {t("common.back")}
            </Typography>
          </>
        );
      case "disable":
        return <></>;
      default:
        return <></>;
    }
  };

  const renderSaveButton = useMemo(() => {
    switch (showSave) {
      case "show":
        return (
          <Button
            className="mr-1 float-right"
            outline
            size="sm"
            onClick={onClickSave}
          >
            {t("common.save")}
          </Button>
        );
      case "disable":
        return (
          <Button className="mr-1 float-right" outline size="sm" disabled>
            {t("common.save")}
          </Button>
        );
      default:
        return <></>;
    }
  }, [t, showSave, onClickSave]);

  const renderCancelButton = useMemo(() => {
    switch (showCancel) {
      case "show":
        return (
          <Button
            className="mr-1 float-right"
            outline
            size="sm"
            onClick={onClickCancel}
          >
            {t("common.cancel")}
          </Button>
        );
      case "disable":
        return (
          <Button className="mr-1 float-right" outline size="sm" disabled>
            {t("common.cancel")}
          </Button>
        );
      default:
        return <></>;
    }
  }, [t, showCancel, onClickCancel]);

  useEffect(() => {
    if (saveOK) dispatch(resetSaveOK());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderCustomButton = () => (showCustom ? <>{children}</> : <></>);

  const renderInfoSuccess = useMemo(() => {
    if (saveOK) {
      window.scroll(0, 0);
      return (
        <InfoPanel
          color="success"
          fullWidth
          message={t("common.saveSuccess")}
          timeoutSec={3}
        />
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const renderInfoError = useMemo(() => {
    if (errors !== undefined && errors.length > 0) {
      window.scroll(0, 0);
      return (
        <InfoPanel color="danger" fullWidth dismissible message="">
          <b>Greška kod snimanja podataka</b>
          <br></br>
          <ul>
            {errors.map((error) => (
              <li>{error.message}</li>
            ))}
          </ul>
        </InfoPanel>
      );
    }
  }, [errors]);

  const renderSaveAndCancelButtons = () => {
    return (
      <div className="d-flex">
        {renderSaveButton}
        {renderCancelButton}
      </div>
    );
  };

  return (
    <>
      <div className={styles.toolbar}>
        <Row className="pl-0">
          <Col lg="3">
            <Col lg="12" className="pl-2">
              {renderBackButton()}
            </Col>
          </Col>
          <Col lg="6" className="pl-0">
            {renderInfoSuccess}
            {renderInfoError}
          </Col>
          <Col lg="3">
            <Col lg="12">
              {renderCustomButton()}
              {renderAddButton}
              {renderEditButton}
              {renderDeleteButton}
              {renderCancelButton}
            </Col>
          </Col>
          <Col lg="12">{renderSaveAndCancelButtons()}</Col>
        </Row>
        <Row>
          <Col lg="12">
            <Typography variant="mediumBold" className="mb-0 text-uppercase">
              {pageTitle}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col lg="12">
            <Typography variant="mediumBold" className="mb-0 text-uppercase">
              {pageSubTitle}
            </Typography>
          </Col>
        </Row>
      </div>
    </>
  );
};
